// Persistent context header for the submission wizard (Figma: blue card with
// progress bar, "Step N/4", "Closes in N days", breakdown title, reassurance
// line). Never renders "Closes in NaN days": each slot holds a placeholder
// until its data arrives.
//
// The card's height must NOT change between those states. It sits above the
// form on every step, so a card that grows when the breakdown resolves — and
// again when the country lookup answers — pushes the fields the visitor is
// already typing into. So the layout is fixed: the same four rows always
// render, at the same heights, and only their CONTENT swaps. Every row below
// therefore carries an explicit line-height/min-height rather than leaning on
// the font's natural line box, which is what differed between a text node and
// a placeholder bar.

import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";
import i18next from "i18next";
import { iRootState } from "../../../store";
import { getDeadlineInfo } from "./deadline";
import { trialReassurance } from "./trialPolicy";
import { openBreakdownUrl } from "./openBreakdownUrl";
import { isRoleUnavailable } from "../../../models/submissionFlowModel";
import { trackSubmissionEvent } from "../analytics";
import { onPlan } from "../../../models/submissionStep4Model";
import { isSubmissionAuthed } from "../../../helpers/submissionAuth";

const TICK_MS = 30 * 1000;
const TOTAL_STEPS = 4;

interface OwnProps {
  step: number;
}

type Props = OwnProps & StateProps & DispatchProps;

const SubmissionHeader: React.FC<Props> = ({
  step,
  submissionInfo,
  serverOffsetMs,
  breakdownId,
  roleId,
  geoGuess,
  geoResolved,
  loadGeoGuess,
  membership,
  loadMembership,
}) => {
  const [, setTick] = useState(0);
  // A signed-in actor may be on Pro or a trial already, and then the trial line
  // would promise them something they have: the line waits for the answer.
  const [planKnown, setPlanKnown] = useState(!isSubmissionAuthed() || !!membership);

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), TICK_MS);
    return () => clearInterval(interval);
  }, []);

  // The reassurance row waits for the country, and only the entry pages and
  // step 1 used to ask for it. Any step reached by a full page load — a
  // refresh, the return from Stripe, a shared link — therefore sat on a grey
  // placeholder bar forever, and step 2's country gate never fired because it
  // had nothing to compare. The header is on every step, so it asks. Before
  // the early return below: hooks run whether or not the card renders.
  useEffect(() => {
    if (!geoResolved && !geoGuess) loadGeoGuess();
    if (!planKnown) Promise.resolve(loadMembership()).then(() => setPlanKnown(true), () => setPlanKnown(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Role-less flow (entered from the expired gate): there is no breakdown to
  // describe, so render nothing rather than a skeleton that never resolves.
  if (!breakdownId && !submissionInfo) return null;

  // The step is a prop, so the progress bar and the chip are correct from the
  // first paint — only the breakdown and the country have to be waited for.
  const deadline = submissionInfo
    ? getDeadlineInfo(submissionInfo.closingDateUtc, serverOffsetMs)
    : null;
  const countryCode = ((geoGuess && geoGuess.countryCode) || "").toUpperCase();
  // Past the deadline but still taking submissions (the API's isLate): the
  // pill says Expired, and one line says why it still goes. The margin itself
  // is never shown, so the deadline that passed stays the urgency.
  const late = !!(submissionInfo && submissionInfo.isLate);

  return (
    <Card data-header-card aria-busy={submissionInfo ? undefined : "true"}>
      <ProgressRow>
        {Array.apply(null, Array(TOTAL_STEPS)).map((_: any, i: number) => (
          <ProgressSegment key={i} active={i < step} />
        ))}
      </ProgressRow>
      <TopRow>
        <StepChip
          role="progressbar"
          aria-valuenow={step}
          aria-valuemin={1}
          aria-valuemax={TOTAL_STEPS}
        >
          {i18next.t("Step {{step}}/{{total}}", { step, total: TOTAL_STEPS })}
        </StepChip>
        <ClosesPill aria-live="polite" late={late}>
          {deadline ? (
            <>
              {deadline.label}
              <ClockIcon />
            </>
          ) : (
            <Placeholder width="86px" />
          )}
        </ClosesPill>
      </TopRow>
      <Title title={(submissionInfo && submissionInfo.breakdownName) || undefined}>
        {/* Plain text, unless the role was cast or the breakdown closed since
            they started (they still send): then it opens the full breakdown in
            a new tab, so they can look closer without leaving the step. */}
        {submissionInfo && isRoleUnavailable(submissionInfo, roleId) ? (
          <TitleLink
            href={openBreakdownUrl(submissionInfo.classicRef || submissionInfo.breakdownId)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              trackSubmissionEvent("submission_casting_call_details_opened", {
                breakdownId: submissionInfo.breakdownId,
                from: `step${step}`,
              })
            }
          >
            {submissionInfo.breakdownName}
            <span aria-hidden="true"> ↗</span>
          </TitleLink>
        ) : submissionInfo ? (
          submissionInfo.breakdownName
        ) : (
          <Placeholder width="70%" />
        )}
      </Title>
      {/* Country-dependent, from GET References/country/code. The TEXT is held
          back until the guess resolves rather than rendered with a default:
          swapping the promise after the fact reads as a bait-and-switch on the
          one line that makes a claim about money. The ROW is not held back —
          reserving its height is what keeps the card from growing under the
          form when the lookup lands. A cached country makes this instant.
          A free casting call needs no country: it is free wherever they are. */}
      {late && (
        <LateLine>
          <InfoIcon />
          {i18next.t("Casting hasn't closed it yet. Send yours before they choose.")}
        </LateLine>
      )}
      <Reassurance>
        {submissionInfo && planKnown && (onPlan(membership) || geoResolved || submissionInfo.allowFreeSubmissions) ? (
          <>
            <InfoIcon />
            {onPlan(membership)
              ? membership!.membershipStatus === "trial"
                ? i18next.t("Included in your free PRO trial.")
                : i18next.t("Included in your PRO membership.")
              : trialReassurance(
                  countryCode,
                  submissionInfo.trialDays,
                  !!submissionInfo.allowFreeSubmissions
                )}
          </>
        ) : (
          <Placeholder width="58%" faint />
        )}
      </Reassurance>
    </Card>
  );
};

// #0584d7 is Figma's value, and also the fix for a contrast problem: white
// 12px text on the old #0f9bf0 was about 3.0:1, well under what small text
// needs. This is roughly 4.0:1, and the heavier weights below carry the rest.
const Card = styled.div`
  background-color: #0584d7;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(5, 70, 130, 0.18);
  padding: 12px 16px 14px;
  margin: 8px auto 0;
  /* No max-width: the card tracks the content column, which steps up on large
     screens (common/breakpoints.ts). A 420px cap left it visibly narrower than
     the form it heads. */
  width: 100%;
  color: white;
`;

const ClockIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    aria-hidden="true"
    style={{ flex: "none", marginLeft: 6 }}
  >
    <circle cx="6" cy="6" r="5" fill="none" stroke="currentColor" strokeWidth="1.4" />
    <path d="M6 3.4V6l1.8 1.2" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const InfoIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    aria-hidden="true"
    style={{ flex: "none", marginRight: 5 }}
  >
    <circle cx="6" cy="6" r="5" fill="none" stroke="currentColor" strokeWidth="1.3" />
    <path d="M6 5.4v3M6 3.5v.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const ProgressRow = styled.div`
  display: flex;
  margin-bottom: 10px;

  /* Spacing lives on the row, not on the segment. In styled-components v3 the
     class name hashes the EVALUATED css, so a prop-dependent segment gets two
     different classes and a "& + &" rule would only match same-state
     neighbours — skipping the gap at the active/inactive boundary. */
  & > * + * {
    margin-left: 6px;
  }
`;

const ProgressSegment: any = styled.div`
  flex: 1;
  height: 4px;
  border-radius: 2px;
  /* Figma draws the steps still to come in a dark blue, not a pale tint:
     against the new card colour a translucent white barely read as a bar. */
  background-color: #005f9c;

  ${(p: any) =>
    p.active &&
    css`
      background-color: #ffffff;
    `}
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const StepChip = styled.div`
  background-color: #0a1f44;
  color: white;
  font-size: 13px;
  font-weight: 400;
  padding: 4px 10px;
  border-radius: 4px;
`;

const ClosesPill: any = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  /* Fixed line box: a placeholder bar and a text node must occupy the same
     height, or the row changes height when the deadline arrives. */
  line-height: 18px;
  min-height: 18px;

  ${(p: any) =>
    p.late &&
    css`
      background-color: #ffe2b8;
      color: #7a3f00;
      border-radius: 12px;
      padding: 0 10px;
    `}
`;

// One line, above the trial line: why an expired casting call still goes.
const LateLine = styled.div`
  display: flex;
  align-items: flex-start;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  margin-top: 6px;

  & > svg {
    margin-top: 2px;
  }
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 800;
  line-height: 24px;
  min-height: 24px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// White on the card, underlined: the global stylesheet colours a:link blue.
const TitleLink = styled.a`
  &:link,
  &:visited,
  &:hover {
    color: white;
  }
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 3px;

  &:focus-visible {
    outline: 2px solid white;
    outline-offset: 2px;
  }
`;

const Reassurance = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  /* Always occupied: while the country is unknown this row holds a placeholder
     instead of collapsing, so the card does not grow when the lookup lands. */
  min-height: 16px;
  margin-top: 6px;
  opacity: 0.95;
`;

// Sits INSIDE a row that already fixes its own height, so this only ever
// stands in for the text — it never sets the layout.
const Placeholder: any = styled.span`
  display: inline-block;
  vertical-align: middle;
  height: 10px;
  border-radius: 4px;
  background-color: rgba(255, 255, 255, ${(p: any) => (p.faint ? 0.22 : 0.35)});
  width: ${(p: any) => p.width || "50%"};
  /* Nudged up to sit on the text's optical centre rather than its baseline. */
  margin-bottom: 2px;
`;

interface StateProps {
  submissionInfo: iRootState["submissionFlowModel"]["submissionInfo"];
  serverOffsetMs: number;
  breakdownId: string | null;
  roleId: string | null;
  geoGuess: iRootState["submissionFlowModel"]["geoGuess"];
  geoResolved: boolean;
  membership: iRootState["submissionStep4Model"]["membership"];
}

const mapStateToProps = (state: iRootState): StateProps => ({
  submissionInfo: state.submissionFlowModel.submissionInfo,
  serverOffsetMs: state.submissionFlowModel.serverOffsetMs,
  breakdownId: state.submissionFlowModel.breakdownId,
  roleId: state.submissionFlowModel.roleId,
  geoGuess: state.submissionFlowModel.geoGuess,
  geoResolved: state.submissionFlowModel.geoResolved,
  membership: state.submissionStep4Model ? state.submissionStep4Model.membership : null,
});

interface DispatchProps {
  loadGeoGuess: () => void;
  loadMembership: () => Promise<any> | void;
}

// Guarded because the layout tests mount the header against a minimal store
// whose dispatch is a plain function with no model namespaces on it.
const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  loadGeoGuess: () => {
    if (dispatch && dispatch.submissionFlowModel) {
      dispatch.submissionFlowModel.loadGeoGuess();
    }
  },
  loadMembership: () =>
    dispatch && dispatch.submissionStep4Model ? dispatch.submissionStep4Model.loadMembership() : undefined,
});

export default connect(mapStateToProps, mapDispatchToProps)(SubmissionHeader);
